/**
 * AttachmentsBox — read-only list + uploader for request attachments.
 * Extracted from CompanyRegistrationDetailPage.jsx during the iter-238 refactor.
 */
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Paperclip, Loader2, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { SectionCard } from './ui';
import { fileToDataUrl } from './helpers';

export function AttachmentsBox({ reg, onSave, saving, t }) {
  const list = reg.attachments || [];
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        // Guard against very large files (~10 MB) — the whole file is
        // stored as a data URL, so this cap keeps the doc size reasonable.
        if (f.size > 10 * 1024 * 1024) {
          toast.error(t(`الملف ${f.name} أكبر من 10MB`, `${f.name} exceeds 10MB`));
          continue;
        }
        const dataUrl = await fileToDataUrl(f);
        await onSave({
          attachment_add: {
            name: f.name,
            label: f.name,
            mime_type: f.type,
            size: f.size,
            data_url: dataUrl,
          },
        });
      }
      toast.success(t('تمت إضافة المرفقات', 'Attachments added'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل رفع المرفق', 'Attachment upload failed'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <SectionCard icon={Paperclip} iconColor="#a855f7" title={t('المرفقات', 'Attachments')}>
      {list.length === 0 ? (
        <div className="text-xs text-[#71717A]">{t('لا توجد مرفقات', 'No attachments')}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {list.map((a, i) => (
            <a
              key={i}
              href={a.data_url}
              download={a.name}
              className="flex items-center gap-2 p-3 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e] hover:bg-[#151530] text-sm"
              data-testid={'attach-' + i}
            >
              <Paperclip className="h-4 w-4 text-purple-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-white truncate">{a.label || a.name}</div>
                <div className="text-xs text-[#606060] truncate">{a.name}</div>
              </div>
            </a>
          ))}
        </div>
      )}
      <div className="mt-4">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="attach-add-input"
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={uploading || saving}
          className="bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/40 text-purple-200 gap-2"
          data-testid="attach-add-btn"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {t('إضافة مرفقات أخرى', 'Upload more attachments')}
        </Button>
      </div>
    </SectionCard>
  );
}
